import React, { useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';

/**
 * Sinal que a aba carrega além do rótulo.
 *
 * `error`  - há campo inválido lá dentro. É o que impede o clássico "cliquei em
 *            Salvar e nada aconteceu": o formulário é um só, mas o campo
 *            recusado pode estar numa aba que a pessoa nem abriu.
 * `draft`  - há alteração digitada e ainda não salva naquela aba.
 */
export type TabStatus = 'error' | 'draft';

export interface TabItem<T extends string = string> {
  value: T;
  label: React.ReactNode;
  icon?: React.ReactNode;
  status?: TabStatus;
  /** Texto do marcador, para leitor de tela e tooltip. Tem padrão por status. */
  statusTitle?: string;
}

export interface TabsProps<T extends string = string> {
  value: T;
  onChange: (value: T) => void;
  tabs: TabItem<T>[];
  /** Rótulo acessível da barra de abas (ex.: "Seções do perfil da loja"). */
  ariaLabel: string;
  /**
   * Prefixo dos ids de `tab`/`tabpanel`. Necessário quando há mais de uma barra
   * de abas na mesma tela - sem ele os `aria-controls` colidiriam.
   */
  idPrefix?: string;
  className?: string;
}

const DEFAULT_STATUS_TITLE: Record<TabStatus, string> = {
  error: 'Há campos a corrigir nesta aba',
  draft: 'Alterações não salvas nesta aba',
};

const Bar = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0.25rem;
  border-bottom: 1px solid ${(p) => p.theme.border};
  /* A barra rola sozinha no celular; a página nunca rola na horizontal. */
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  position: relative;
  border: none;
  background: transparent;
  padding: 0.7rem 1rem;
  margin-bottom: -1px;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  white-space: nowrap;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 700;
  color: ${(p) => (p.$active ? p.theme.primary : p.theme.textMuted)};
  border-bottom: 2px solid ${(p) => (p.$active ? p.theme.primary : 'transparent')};
  transition: color 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    color: ${(p) => (p.$active ? p.theme.primary : p.theme.textSecondary)};
  }

  &:focus-visible {
    outline: none;
    border-radius: ${(p) => p.theme.radius} ${(p) => p.theme.radius} 0 0;
    box-shadow: 0 0 0 3px ${(p) => p.theme.primary}22;
  }
`;

/**
 * Bolinha de estado. Cor e nada mais seria invisível para quem não distingue
 * cor, por isso o `title`/`aria-label` viaja junto.
 */
const StatusDot = styled.span<{ $status: TabStatus }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(p) => (p.$status === 'error' ? p.theme.error : p.theme.accent)};
`;

const Panel = styled.div`
  &:focus {
    outline: none;
  }
`;

const tabId = (prefix: string, value: string) => `${prefix}-tab-${value}`;
const panelId = (prefix: string, value: string) => `${prefix}-panel-${value}`;

/**
 * Barra de abas de página (Imagens | Dados | Descoberta).
 *
 * Existe ao lado do `SegmentedControl` e não no lugar dele: aquele é um par de
 * pílulas para alternar a *mesma* lista (Lista/Kanban) e não tem onde pendurar
 * estado por opção. Aqui a aba precisa dizer "tem erro aqui dentro" e "tem
 * rascunho aqui dentro" - sem isso, num formulário único repartido em abas o
 * Salvar simplesmente não responde e ninguém descobre por quê.
 *
 * Navegação por teclado segue o padrão de `tablist`: ←/→ andam entre as abas,
 * Home/End vão para a primeira/última.
 */
export function Tabs<T extends string = string>({
  value,
  onChange,
  tabs,
  ariaLabel,
  idPrefix = 'tabs',
  className,
}: TabsProps<T>) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const values = useMemo(() => tabs.map((tab) => tab.value), [tabs]);

  const focusTab = useCallback(
    (next: T) => {
      onChange(next);
      refs.current[next]?.focus();
    },
    [onChange]
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const index = values.indexOf(value);
    if (index < 0) return;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusTab(values[(index + 1) % values.length]);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusTab(values[(index - 1 + values.length) % values.length]);
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusTab(values[0]);
    } else if (event.key === 'End') {
      event.preventDefault();
      focusTab(values[values.length - 1]);
    }
  };

  return (
    <Bar role="tablist" aria-label={ariaLabel} className={className}>
      {tabs.map((tab) => {
        const active = tab.value === value;
        const statusTitle = tab.status ? tab.statusTitle ?? DEFAULT_STATUS_TITLE[tab.status] : undefined;
        return (
          <Tab
            key={tab.value}
            id={tabId(idPrefix, tab.value)}
            ref={(node: HTMLButtonElement | null) => {
              refs.current[tab.value] = node;
            }}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={panelId(idPrefix, tab.value)}
            // Só a aba ativa recebe Tab; as outras se alcançam pelas setas.
            tabIndex={active ? 0 : -1}
            $active={active}
            onClick={() => onChange(tab.value)}
            onKeyDown={handleKeyDown}
          >
            {tab.icon}
            {tab.label}
            {tab.status && <StatusDot $status={tab.status} title={statusTitle} aria-label={statusTitle} />}
          </Tab>
        );
      })}
    </Bar>
  );
}

export interface TabPanelProps {
  /** Aba a que este painel pertence; casa com `TabItem.value`. */
  tab: string;
  idPrefix?: string;
  className?: string;
  children: React.ReactNode;
}

/** Conteúdo de uma aba. Renderize apenas o painel ativo. */
export const TabPanel: React.FC<TabPanelProps> = ({ tab, idPrefix = 'tabs', className, children }) => (
  <Panel
    id={panelId(idPrefix, tab)}
    role="tabpanel"
    aria-labelledby={tabId(idPrefix, tab)}
    tabIndex={-1}
    className={className}
  >
    {children}
  </Panel>
);

export default Tabs;
